class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.instanceId = `predictive-${++PredictiveSearch.instanceCount}`;
    this.resultsId = `predictive-search-results-${this.instanceId}`;
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.setAttribute('aria-owns', this.resultsId);
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestId = 0;
    this.pendingSearchTerm = null;
    this.searchDismissed = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('click', () => {
      if (this.searchDismissed) this.onFocus();
    });
    this.input.addEventListener('input', () => {
      this.searchDismissed = false;
      const query = this.getQuery();
      if (!query.length) this.close(true);
      else if (this.pendingSearchTerm !== null && query !== this.pendingSearchTerm) this.cancelSearchRequest();
    });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    if (this.searchDismissed || !this.isConnected) return;
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('[data-predictive-groups]')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  shouldResetForm() {
    return true;
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (this.shouldResetForm()) {
      this.searchTerm = '';
      this.close(true);
    }
  }

  onFocus() {
    if (this.suppressSearchFocus) return;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;
    this.searchDismissed = false;
    if (this.pendingSearchTerm === currentSearchTerm) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
    // Disabling a focused Retry can synchronously blur it to body in browsers.
    // That browser action is not a shopper's decision to leave suggestions.
    if (this.disablingRetry && !event?.relatedTarget) return;
    if (event?.relatedTarget) {
      if (!this.contains(event.relatedTarget)) this.dismissSearch();
      return;
    }
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.dismissSearch();
    });
  }

  disconnectedCallback() {
    this.dismissSearch();
  }

  dismissSearch(focusInput = false) {
    this.searchDismissed = true;
    this.cancelSearchRequest();
    this.close();
    clearTimeout(this.statusTimer);
    const status = this.statusElement || this.querySelector('.predictive-search-status');
    if (status) { status.textContent = ''; status.setAttribute('aria-hidden', 'true'); }
    if (focusInput && this.isConnected) {
      this.suppressSearchFocus = true;
      try { this.input.focus({ preventScroll: true }); } finally { this.suppressSearchFocus = false; }
    }
  }

  onKeyup(event) {
    if (event.isComposing) return;
    const key = event.key === 'Enter' ? 'Enter' : event.code || event.key;
    if (event.target !== this.input && (key === 'Enter' || key === 'Space' || key === ' ')) return;
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (key) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.target === this.input && !event.isComposing && this.getQuery().length
      && (event.key === 'Enter' || event.code === 'Enter' || event.code === 'NumpadEnter')
      && this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]')) {
      event.preventDefault();
      return;
    }
    if (event.code === 'Escape' && this.getQuery().length) {
      event.preventDefault();
      this.dismissSearch(true);
      return;
    }
    if (event.code === 'ArrowDown' && this.searchDismissed) this.onFocus();
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;
    const parts = currentButtonText.split(previousTerm);
    // Only update an unambiguous literal query, including punctuation and replacement characters.
    if (parts.length === 2) searchForTextElement.innerText = `${parts[0]}${newTerm}${parts[1]}`;
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm, retrying = false) {
    if (this.searchDismissed || !this.isConnected) return Promise.resolve(false);
    if (!searchTerm.trim()) { this.close(true); return; }
    const active = document.activeElement;
    const retryFocus = retrying && (active === this.input || active === this.querySelector('[data-predictive-retry]'))
      ? active : null;
    this.cancelSearchRequest();
    const requestId = this.requestId;
    const queryKey = searchTerm;
    const previousError = this.querySelector('[data-predictive-error]');
    if (previousError && previousError.dataset.query !== searchTerm) {
      this.close();
      this.predictiveSearchResults.replaceChildren();
      this.removeAttribute('results');
    }
    this.setLiveRegionLoadingState();
    const retryButton = this.querySelector('[data-predictive-retry]');
    if (retryButton) {
      this.disablingRetry = true;
      try { retryButton.disabled = true; retryButton.textContent = 'Trying again…'; }
      finally { this.disablingRetry = false; }
    }

    if (this.cachedResults[queryKey]) {
      try {
        if (!this.isSearchRequestCurrent(requestId, searchTerm, this.abortController.signal, retryFocus)) return false;
        this.renderSearchResults(this.cachedResults[queryKey]);
        const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
        this.restoreRetryFocus(this.input, retryFocus);
        return;
      } catch (_) {
        this.allPredictiveSearchInstances.forEach(instance => { delete instance.cachedResults[queryKey]; });
      }
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    const requestController = this.abortController;
    const requestSignal = requestController.signal;
    this.pendingSearchTerm = searchTerm;

    return this.fetchSearchResponse(searchTerm, requestController)
      .then((text) => {
        if (!this.isSearchRequestCurrent(requestId, searchTerm, requestSignal, retryFocus)) {
          searchDeferred?.reject(new DOMException('The search query changed.', 'AbortError'));
          return;
        }
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.renderSearchResults(resultsMarkup);
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
        this.restoreRetryFocus(this.input, retryFocus);
      })
      .catch((error) => {
        if (requestId !== this.requestId || this.getQuery() !== searchTerm || this.searchDismissed || !this.isConnected || error?.name === 'AbortError' || (requestSignal.aborted && requestSignal.reason?.name !== 'TimeoutError')) {
          searchDeferred?.reject(error);
          return;
        }
        if (!this.isSearchRequestCurrent(requestId, searchTerm, requestSignal, retryFocus, true)) {
          searchDeferred?.reject(new DOMException('The search interaction ended.', 'AbortError'));
          return;
        }
        searchDeferred?.reject(error);
        this.showRequestFailure(searchTerm, retrying, retryFocus);
      })
      .finally(() => {
        if (requestId === this.requestId) this.pendingSearchTerm = null;
      });
  }

  isSearchRequestCurrent(requestId, query, signal, retryFocus, allowTimeout = false) {
    if (requestId !== this.requestId || this.getQuery() !== query || this.searchDismissed || !this.isConnected
      || (signal.aborted && !(allowTimeout && signal.reason?.name === 'TimeoutError'))) return false;
    const active = document.activeElement;
    const retryBlur = active === document.body && retryFocus && (!retryFocus.isConnected || retryFocus.disabled);
    if (active && !this.contains(active) && !retryBlur) {
      this.dismissSearch();
      return false;
    }
    return true;
  }

  restoreRetryFocus(target, initiator) {
    if (!initiator || !target?.isConnected || this.searchDismissed || !this.isConnected) return;
    const active = document.activeElement;
    const retired = !initiator.isConnected || initiator.disabled;
    if (active !== initiator && !(retired && active === document.body)) return;
    // Keyboard listbox activation can leave focus on the input itself.
    const destination = initiator === this.input ? this.input : target;
    if (active !== destination) destination.focus({ preventScroll: true });
  }

  cancelSearchRequest() {
    ++this.requestId;
    this.abortController.abort();
    this.abortController = new AbortController();
    this.pendingSearchTerm = null;
    if (this.hasAttribute('loading')) {
      clearTimeout(this.statusTimer);
      const status = this.statusElement || this.querySelector('.predictive-search-status');
      if (status) { status.textContent = ''; status.setAttribute('aria-hidden', 'true'); }
    }
    this.removeAttribute('loading');
    this.predictiveSearchResults.setAttribute('aria-busy', 'false');
    const retryButton = this.querySelector('[data-predictive-retry]');
    if (retryButton) { retryButton.disabled = false; retryButton.textContent = 'Retry suggestions'; }
  }

  fetchSearchResponse(searchTerm, controller) {
    const signal = controller.signal;
    let onAbort;
    const aborted = new Promise((resolve, reject) => {
      onAbort = () => reject(signal.reason || new DOMException('The search request was cancelled.', 'AbortError'));
      signal.addEventListener('abort', onAbort, { once: true });
    });
    const timer = setTimeout(() => controller.abort(new DOMException('Search suggestions took too long to load.', 'TimeoutError')), PredictiveSearch.requestTimeout);
    const response = (async () => {
      const response = await fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,page&section_id=predictive-search`, { signal });
      if (!response.ok) throw new Error(response.status);
      return response.text();
    })();
    // Bound both the request and its response body, including a fetch that never settles.
    return Promise.race([response, aborted]).finally(() => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
    });
  }

  showRequestFailure(searchTerm, retrying = false, retryFocus = null) {
    const panel = document.createElement('div');
    panel.setAttribute('data-predictive-error', '');
    panel.dataset.query = searchTerm;
    const message = document.createElement('p');
    message.className = 'predictive-search__heading';
    message.setAttribute('role', 'alert');
    message.textContent = 'Search suggestions couldn’t load. Try again, or open the full search results.';
    const options = document.createElement('div');
    options.id = this.resultsId;
    options.setAttribute('role', 'listbox');
    options.setAttribute('aria-label', 'Search recovery options');
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.id = `predictive-retry-${this.instanceId}`;
    retry.className = 'predictive-search__item link link--text';
    retry.setAttribute('data-predictive-retry', '');
    retry.setAttribute('role', 'option');
    retry.setAttribute('aria-selected', 'false');
    retry.textContent = 'Retry suggestions';
    retry.addEventListener('click', () => this.getSearchResults(this.getQuery(), true));
    const list = document.createElement('ul');
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'presentation');
    const option = document.createElement('li');
    option.id = `predictive-full-search-${this.instanceId}`;
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', 'false');
    const link = document.createElement('a');
    link.className = 'predictive-search__item link link--text';
    link.textContent = 'Open full search results';
    const params = new URLSearchParams();
    this.input.form.querySelectorAll('input[type="hidden"][name]').forEach(input => { params.append(input.name, input.value); });
    params.set('q', searchTerm);
    const action = this.input.form.getAttribute('action') || '/search';
    link.href = `${action}${action.includes('?') ? '&' : '?'}${params.toString()}`;
    option.append(link);
    list.append(option);
    options.append(retry, list);
    panel.append(message, options);
    this.predictiveSearchResults.replaceChildren(panel);
    this.input.removeAttribute('aria-activedescendant');
    this.predictiveSearchResults.setAttribute('aria-busy', 'false');
    this.removeAttribute('loading');
    this.setAttribute('results', true);
    this.setLiveRegionText(message.textContent);
    this.open();
    if (retrying) this.restoreRetryFocus(retry, retryFocus);
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    const event = new SearchUpdateEvent({ search: { query }, promise: deferred.promise });
    // This owner's cancellation/failure remains rejected for event consumers,
    // while its visible recovery handles that rejection locally.
    event.promise?.catch(() => {});
    this.dispatchEvent(event);
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
    this.predictiveSearchResults.setAttribute('aria-busy', 'true');
  }

  setLiveRegionText(statusText) {
    clearTimeout(this.statusTimer);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    this.statusTimer = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    const template = document.createElement('template');
    template.innerHTML = resultsMarkup;
    if (!template.content.querySelector('#predictive-search-results') || !template.content.querySelector('[data-predictive-search-live-region-count-value]')) throw new Error('Incomplete search suggestions.');
    template.content.querySelector('#predictive-search-results-groups-wrapper')?.setAttribute('data-predictive-groups', '');
    const ids = new Map();
    template.content.querySelectorAll('[id]').forEach(element => {
      const oldId = element.id;
      const newId = `${oldId}-${this.instanceId}`;
      ids.set(oldId, newId);
      element.id = newId;
    });
    template.content.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], [aria-owns]').forEach(element => {
      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'].forEach(attribute => {
        if (element.hasAttribute(attribute)) element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map(id => ids.get(id) || id).join(' '));
      });
    });
    this.predictiveSearchResults.replaceChildren(template.content);
    this.input.removeAttribute('aria-activedescendant');
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.predictiveSearchResults.setAttribute('aria-busy', 'false');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.wi-header-section') || document.querySelector('.section-header');
    this.resultsMaxHeight = Math.max(96, window.innerHeight - (header?.getBoundingClientRect().bottom || 0) - 24);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.cancelSearchRequest();
      this.predictiveSearchResults.replaceChildren();
      clearTimeout(this.statusTimer);
      const status = this.statusElement || this.querySelector('.predictive-search-status');
      if (status) { status.textContent = ''; status.setAttribute('aria-hidden', 'true'); }
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.predictiveSearchResults.setAttribute('aria-busy', 'false');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

PredictiveSearch.instanceCount = 0;
PredictiveSearch.requestTimeout = 10000;
customElements.define('predictive-search', PredictiveSearch);
